import { signInWithEmailAndPassword } from "firebase/auth";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";

import { auth } from "../../firebase";
import logo from "../../assets/images/logo.png";
import taxiImage from "../../assets/images/taxi.jpg";
import { useAuth } from "../../context/AuthContext";

const SignIn = ({ adminRequired = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();

  useEffect(() => {
    if (currentUser && adminRequired && !isAdmin()) {
      logout();
      setError("Access denied. Admin privileges required.");
    }
  }, [currentUser, adminRequired, logout, isAdmin]);

  const handleSignIn = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      if (error.code === "auth/invalid-credential") {
        setError("Invalid Credentials");
      } else {
        setError("Authentication error. Please try again.");
        console.error("Sign-in error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  const handleBackButton = () => {
    window.history.pushState(null, null, window.location.pathname);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg overflow-hidden flex">
        {/* Image Section */}
        <div className="hidden md:block md:w-1/2 bg-cover relative">
          <div className="absolute inset-0">
            <img
              src={taxiImage}
              alt="Taxi"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Form Section */}
        <div className="w-full md:w-1/2 p-8 flex flex-col justify-center">
          <motion.div
            className="w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-6">
              <img
                src={logo}
                alt="Logo"
                className="mx-auto mb-4"
                height={75}
                width={150}
              />
              <h2 className="text-center text-3xl font-bold text-primary">
                LOGIN
              </h2>
            </div>
            <form
              className="space-y-6"
              onSubmit={handleSignIn}
            >
              <div className="space-y-4">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <label
                    htmlFor="email-address"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="Email address"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </motion.div>
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="Password"
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </motion.div>
              </div>

              {error && (
                <motion.p
                  className="text-red-500 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.p>
              )}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary bg-celadon focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-gray-400"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" /> Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
